;
// Artist Pool Types
/**
* ArtistPoolType represents the type of an artist pool.
* It can be either 'fixed', 'dynamic', 'blended', or 'shared'.
*/
export var ArtistPoolType;
(function (ArtistPoolType) {
    ArtistPoolType["FIXED"] = "fixed";
    ArtistPoolType["DYNAMIC"] = "dynamic";
    ArtistPoolType["BLENDED"] = "blended";
    ArtistPoolType["SHARED"] = "shared";
})(ArtistPoolType || (ArtistPoolType = {}));
// Coral Types
/**
* CoralType represents the type of a coral.

* Corals can be of different types:
* - Shared: An existing base Coral that can added to a user's library
* - User: A Coral that is created manually by a user adding artists
*/
export var CoralType;
(function (CoralType) {
    CoralType["SHARED"] = "shared";
    CoralType["USER"] = "userManual";
    CoralType["PLATFORM"] = "platform";
})(CoralType || (CoralType = {}));
// Refund Types
export var RefundType;
(function (RefundType) {
    RefundType["FULL"] = "FULL";
    RefundType["UNDISTRIBUTED"] = "UNDISTRIBUTED";
    RefundType["UNCLAIMED"] = "UNCLAIMED";
    RefundType["ARTIST_REFUSED"] = "ARTIST_REFUSED";
    RefundType["OPERATOR_CORRECTION"] = "OPERATOR_CORRECTION"; // Entered by an operator without a member request (e.g. a chargeback); no member emails
})(RefundType || (RefundType = {}));
/**
 * PENDING → PROCESSING (approval in progress; resumable) → APPROVED (amount reversed from artists, owed to
 * the member) → COMPLETED (paid). REJECTED is declined — by an admin from PENDING, or at approval when
 * nothing unpaid is left.
 */
export var RefundStatus;
(function (RefundStatus) {
    RefundStatus["PENDING"] = "PENDING";
    RefundStatus["PROCESSING"] = "PROCESSING";
    RefundStatus["APPROVED"] = "APPROVED";
    RefundStatus["COMPLETED"] = "COMPLETED";
    RefundStatus["REJECTED"] = "REJECTED";
})(RefundStatus || (RefundStatus = {}));
/** The statuses of a request still in progress; a coral has at most one such request at a time. */
export const OPEN_REFUND_STATUSES = [RefundStatus.PENDING, RefundStatus.PROCESSING, RefundStatus.APPROVED];
export const isOpenRefundStatus = (status) => OPEN_REFUND_STATUSES.includes(status);
export var RefundPaymentMethod;
(function (RefundPaymentMethod) {
    RefundPaymentMethod["PAYPAL"] = "PAYPAL";
    RefundPaymentMethod["BANK_TRANSFER"] = "BANK_TRANSFER";
})(RefundPaymentMethod || (RefundPaymentMethod = {}));
/**
* ArtistApplication represents an artist's application to the platform.
* It contains all the necessary information for the application process.
*/
// Define enums for company number types and public profile types
export var CompanyNumberType;
(function (CompanyNumberType) {
    CompanyNumberType["ABN"] = "ABN";
    CompanyNumberType["ACN"] = "ACN";
    CompanyNumberType["EIN"] = "EIN";
    CompanyNumberType["CRN"] = "CRN";
    CompanyNumberType["NZBN"] = "NZBN";
    CompanyNumberType["BN"] = "BN";
    CompanyNumberType["OTHER"] = "OTHER";
    // Add more types as needed
})(CompanyNumberType || (CompanyNumberType = {}));
export var PublicProfileType;
(function (PublicProfileType) {
    PublicProfileType["INSTAGRAM"] = "instagram";
    PublicProfileType["TIKTOK"] = "tiktok";
    PublicProfileType["FACEBOOK"] = "facebook";
    PublicProfileType["TWITTER"] = "twitter";
    PublicProfileType["YOUTUBE"] = "youtube";
    PublicProfileType["WEBSITE"] = "website";
    // Add more types as needed
})(PublicProfileType || (PublicProfileType = {}));
// Enums
/**
* Source represents the source of the artist pool.
* It can be 'Spotify' or any other source you want to add in the future.
*/
export var Source;
(function (Source) {
    Source["UNDEFINED"] = "Undefined";
    Source["USER"] = "User";
    Source["SHARED"] = "Shared";
    // Add other sources here
})(Source || (Source = {}));
/**
* SourceType represents the type of the source of the artist pool.
* It can be 'FavouriteArtists', 'ArtistsOnRotation', or 'ArtistsFollowed'.
*/
export var SourceType;
(function (SourceType) {
    SourceType["UNDEFINED"] = "Undefined";
    SourceType["USER_ADDED"] = "UserAdded";
    SourceType["USER_CONNECTED_CURATED_CORAL"] = "UserConnectedCuratedCoral";
    // Add other source types here}
})(SourceType || (SourceType = {}));
/**
* CoralSubscriptionStatus represents the status of a coral.
* It can be 'Pending', 'Active', 'Paused', or 'Archived'.
*/
export var CoralSubscriptionStatus;
(function (CoralSubscriptionStatus) {
    CoralSubscriptionStatus[CoralSubscriptionStatus["NONE"] = 1] = "NONE";
    CoralSubscriptionStatus[CoralSubscriptionStatus["PENDING"] = 2] = "PENDING";
    CoralSubscriptionStatus[CoralSubscriptionStatus["ACTIVE"] = 3] = "ACTIVE";
    CoralSubscriptionStatus[CoralSubscriptionStatus["PAUSED"] = 4] = "PAUSED";
})(CoralSubscriptionStatus || (CoralSubscriptionStatus = {}));
// Campaign schedule constants — shared between backend cron and frontend UI.
// Cold intro drip: a collect link from the first email, the full claim steps in the final one.
export const INTRO_SCHEDULE_DAYS = [0, 4, 10];
export const INTRO_CAMPAIGN_EMAIL_COUNT = 3;
// Warm intro drip: front-loaded, 2 emails — E1 carries the claim, E2 explains how Coral works.
export const WARM_INTRO_SCHEDULE_DAYS = [0, 4];
export const WARM_INTRO_CAMPAIGN_EMAIL_COUNT = 2;
/**
 * Single source of truth for intro-campaign scheduling. Resolves an (optionally absent) variant to
 * its concrete schedule + email count. Schedule and count are paired here by construction, so a
 * cold-count/warm-schedule desync is structurally impossible. Absent variant ⇒ 'cold' (in-flight
 * campaigns started before the variant field existed). Used by the backend cron, the outreach
 * manager, and the frontend schedule preview so all three agree.
 */
export function getIntroVariantConfig(variant) {
    if ((variant !== null && variant !== void 0 ? variant : 'cold') === 'warm') {
        return { variant: 'warm', schedule: WARM_INTRO_SCHEDULE_DAYS, count: WARM_INTRO_CAMPAIGN_EMAIL_COUNT };
    }
    return { variant: 'cold', schedule: INTRO_SCHEDULE_DAYS, count: INTRO_CAMPAIGN_EMAIL_COUNT };
}
